import { ArrowUpRight, Compass, HeartHandshake, Sparkles, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion/Reveal";
import { SectionIntro } from "@/components/sections/SectionIntro";
import { AUDIENCES, WHY } from "@/content/company";

const WHY_ICONS = [Target, Compass, HeartHandshake, Sparkles];

export const WhyVion = () => (
  <section className="grain relative overflow-hidden bg-ink py-24 sm:py-32" data-testid="home-why">
    <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-plum-3/25 blur-[140px]" />
    <div className="shell relative z-[2]">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionIntro eyebrow="Why VION Tech" title={<>Technology <span className="italic text-gold-metal">With Purpose.</span></>} />
        <Reveal as="p" delay={0.1} className="max-w-sm text-base leading-relaxed text-white/70">Four principles shape how we teach, build and advise.</Reveal>
      </div>
      <ol className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => {
          const Icon = WHY_ICONS[i];
          return (
            <Reveal as="li" key={w.title} delay={i * 0.08} className="h-full">
              <article className="group relative flex h-full min-h-[300px] flex-col overflow-hidden rounded-[24px] border border-white/10 bg-ink-2 p-7 transition-[border-color,transform] duration-700 hover:-translate-y-1 hover:border-gold/50 sm:p-8" data-testid={`why-item-${i + 1}`}>
                <span aria-hidden="true" className="hairline-gold absolute inset-x-0 top-0 h-px opacity-60" />
                <span aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-plum-3/0 blur-3xl transition-colors duration-700 group-hover:bg-plum-3/40" />
                <div className="flex items-start justify-between">
                  <span className="font-display text-5xl font-light italic leading-none text-gold/70 transition-colors duration-500 group-hover:text-gold-light">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold-light"><Icon className="h-5 w-5" strokeWidth={1.4} aria-hidden="true" /></span>
                </div>
                <h3 className="mt-14 font-display text-[1.7rem] font-normal leading-tight text-white">{w.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-white/70">{w.text}</p>
              </article>
            </Reveal>
          );
        })}
      </ol>
    </div>
  </section>
);

export const WhoWeServe = () => (
  <section className="bg-ivory py-24 text-ink sm:py-32" data-testid="home-who-we-serve">
    <div className="shell">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionIntro tone="light" eyebrow="Who We Serve" title={<>Built for people <em className="text-plum-2">and</em> institutions.</>} />
        <Reveal delay={0.1}>
          <Link to="/contact" data-testid="who-we-serve-cta" className="inline-flex min-h-[48px] items-center gap-2 border-b border-ink/30 text-[12px] font-semibold uppercase tracking-[0.16em] text-ink transition-colors hover:border-plum hover:text-plum">
            Find your pathway <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
      <ul className="mt-16 grid grid-cols-1 border-l border-t border-ink/15 sm:grid-cols-2 lg:grid-cols-4">
        {AUDIENCES.map(({ title, icon: Icon }, i) => (
          <Reveal as="li" key={title} delay={(i % 4) * 0.06} className="group relative flex min-h-[170px] flex-col justify-between border-b border-r border-ink/15 p-7 transition-colors duration-500 hover:bg-plum sm:min-h-[210px]" data-testid={`audience-${i + 1}`}>
            <div className="flex items-start justify-between">
              <span className="font-mono text-[11px] text-plum-2 transition-colors group-hover:text-gold-light">{String(i + 1).padStart(2, "0")}</span>
              <Icon className="h-5 w-5 text-ink/50 transition-colors duration-500 group-hover:text-gold-light" strokeWidth={1.4} aria-hidden="true" />
            </div>
            <p className="font-display text-2xl font-light leading-tight text-ink transition-colors duration-500 group-hover:text-white">{title}</p>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);
